import { PageKey } from '@core/enums/page-keys.enum';
import { RecordingType } from '@core/enums/recording-type.enum';
import { FlowId, type CheckinWorkflow } from '../workflow-engine.types';
import { covidAcknowledgementFlow } from './covid-acknowledgement.flow';
import { greetingsWelcomeFlow } from './greetings-welcome.flow';
import { qrCodeValidationFlow } from './qr-code-validation.flow';

export enum DailyCheckinCheckoutStep {
  Selection = 'Selection',
  AfterSelection = 'AfterSelection',
  AfterCovid = 'AfterCovid',
  ScanQr = 'ScanQr',
  AfterQrValidation = 'AfterQrValidation',
  AfterWelcome = 'AfterWelcome',
  DailyCheckin = 'DailyCheckin',
  Goodbye = 'Goodbye',
  DailyCheckout = 'DailyCheckout',
}

export const dailyCheckinCheckoutFlow: CheckinWorkflow<DailyCheckinCheckoutStep> = {
  id: FlowId.DailyCheckinCheckoutFlow,
  entry: DailyCheckinCheckoutStep.Selection,
  steps: {
    [DailyCheckinCheckoutStep.Selection]: {
      id: DailyCheckinCheckoutStep.Selection,
      page: PageKey.DAILY_CHECKIN_CHECKOUT,
      next: (variables) => {
        if (!variables.recordingType) {
          throw new Error('Daily check-in/checkout requires a recording type');
        }
        return { nextStep: DailyCheckinCheckoutStep.AfterSelection };
      },
      back: () => ({ bubble: 'back' }),
      exit: () => ({ bubble: 'exit' }),
    },
    [DailyCheckinCheckoutStep.AfterSelection]: {
      id: DailyCheckinCheckoutStep.AfterSelection,
      next: (variables) =>
        variables.recordingType === RecordingType.CHECK_IN &&
        variables.checkinProcessConfiguration?.hasCoronavirusScreen
          ? { flow: covidAcknowledgementFlow, returnStep: DailyCheckinCheckoutStep.AfterCovid }
          : { nextStep: DailyCheckinCheckoutStep.ScanQr },
      back: () => ({ nextStep: DailyCheckinCheckoutStep.Selection }),
      exit: () => ({ bubble: 'exit' }),
    },
    [DailyCheckinCheckoutStep.AfterCovid]: {
      id: DailyCheckinCheckoutStep.AfterCovid,
      next: () => ({ nextStep: DailyCheckinCheckoutStep.ScanQr }),
    },
    [DailyCheckinCheckoutStep.ScanQr]: {
      id: DailyCheckinCheckoutStep.ScanQr,
      next: () => ({
        flow: qrCodeValidationFlow,
        returnStep: DailyCheckinCheckoutStep.AfterQrValidation,
      }),
      back: (variables) =>
        variables.recordingType === RecordingType.CHECK_IN &&
        variables.checkinProcessConfiguration?.hasCoronavirusScreen
          ? { nextStep: DailyCheckinCheckoutStep.AfterSelection }
          : { nextStep: DailyCheckinCheckoutStep.Selection },
      exit: () => ({ bubble: 'exit' }),
    },
    [DailyCheckinCheckoutStep.AfterQrValidation]: {
      id: DailyCheckinCheckoutStep.AfterQrValidation,
      next: (variables) => {
        if (variables.recordingType === RecordingType.CHECK_IN) {
          return { flow: greetingsWelcomeFlow, returnStep: DailyCheckinCheckoutStep.AfterWelcome };
        }
        if (variables.recordingType === RecordingType.CHECK_OUT) {
          return { nextStep: DailyCheckinCheckoutStep.Goodbye };
        }
        throw new Error('Daily check-in/checkout requires a recording type');
      },
      back: () => ({ nextStep: DailyCheckinCheckoutStep.ScanQr }),
      exit: () => ({ bubble: 'exit' }),
    },
    [DailyCheckinCheckoutStep.AfterWelcome]: {
      id: DailyCheckinCheckoutStep.AfterWelcome,
      next: () => ({ nextStep: DailyCheckinCheckoutStep.DailyCheckin }),
      exit: () => ({ bubble: 'exit' }),
    },
    [DailyCheckinCheckoutStep.DailyCheckin]: {
      id: DailyCheckinCheckoutStep.DailyCheckin,
      page: PageKey.DAILY_CHECKIN,
      next: () => ({ complete: true }),
    },
    [DailyCheckinCheckoutStep.Goodbye]: {
      id: DailyCheckinCheckoutStep.Goodbye,
      page: PageKey.GREETINGS_GOODBYE,
      next: () => ({ nextStep: DailyCheckinCheckoutStep.DailyCheckout }),
      back: () => ({ nextStep: DailyCheckinCheckoutStep.ScanQr }),
      exit: () => ({ bubble: 'exit' }),
    },
    [DailyCheckinCheckoutStep.DailyCheckout]: {
      id: DailyCheckinCheckoutStep.DailyCheckout,
      page: PageKey.DAILY_CHECKOUT,
      next: () => ({ complete: true }),
    },
  },
};
